<script setup lang="ts">
import type { PropType } from 'vue';

import type { ButtonEmits } from '@shared/ui/button';

import { buttonProps, createButtonClickHandler } from '@shared/ui/button';

const props = defineProps({
  ...buttonProps,
  /** `icon` — квадрат 44px под одну иконку, подпись в `aria-label`. */
  size: { type: String as PropType<'sm' | 'md' | 'lg' | 'icon'>, default: 'md' },
});
const emit = defineEmits<ButtonEmits>();
const handleClick = createButtonClickHandler(props, emit);
</script>

<template>
  <button
    :class="[
      'ui-button',
      `ui-button--${size}`,
      {
        'ui-button--outline': outline,
        'ui-button--disabled': disabled,
        'ui-button--loading': loading,
      },
    ]"
    :disabled="disabled || loading"
    @click="handleClick"
  >
    <div
      v-if="loading"
      class="ui-button__loader"
    >
      <svg
        class="ui-button__loader-icon"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          class="ui-button__loader-circle"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          stroke-width="4"
        />
        <path
          class="ui-button__loader-path"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
    </div>
    <slot />
  </button>
</template>

<style scoped>
@reference "../../../tailwind.css";

@layer components {
  .ui-button {
    @apply relative inline-flex cursor-pointer items-center justify-center rounded-xl transition-[transform,background-color,border-color,color,opacity] duration-200 disabled:cursor-not-allowed;
  }

  /* Размеры */
  .ui-button--sm {
    @apply px-3.5 py-2 text-xs;
  }

  .ui-button--md {
    @apply px-6 py-2 text-sm;
  }

  .ui-button--lg {
    @apply px-8 py-2 text-base;
  }

  .ui-button--icon {
    @apply size-11 text-sm;
  }

  /* Основная кнопка */
  .ui-button {
    @apply border-(--button-primary-bg) bg-(--button-primary-bg) text-(--on-accent);
  }

  .ui-button:not(:disabled):not(.ui-button--loading):hover {
    @apply border-(--button-primary-hover) bg-(--button-primary-hover);
  }

  /* Outline вариант. Рамка — inset-ring: размер кнопки не меняется, кольцо фокуса ложится поверх. */
  .ui-button--outline {
    @apply bg-(--button-secondary-bg) text-(--text-primary) inset-ring inset-ring-(--button-secondary-border);
  }

  .ui-button--outline:not(:disabled):not(.ui-button--loading):hover {
    @apply bg-(--button-secondary-hover) text-(--text-primary);
  }

  /* Неактивная кнопка — цвета своего варианта под той же прозрачностью, что у полей формы. */
  .ui-button--disabled {
    @apply cursor-not-allowed opacity-50;
  }

  /* Загрузка: текст прячем цветом, так как правило для дочерних элементов не видит голый текст. Ширина кнопки не меняется. */
  .ui-button--loading {
    @apply cursor-wait text-transparent!;
  }

  .ui-button__loader {
    @apply absolute inset-0 flex items-center justify-center text-(--on-accent);
  }

  .ui-button--outline .ui-button__loader {
    @apply text-(--text-primary);
  }

  .ui-button__loader-icon {
    @apply h-5 w-5;
    /* Крутим весь значок: у дуги внутри SVG центр вращения в углу, и она болтается. */
    animation: spin 1s linear infinite;
  }

  .ui-button__loader-circle {
    @apply opacity-25;
  }

  .ui-button__loader-path {
    @apply opacity-75;
  }

  .ui-button--loading > :not(.ui-button__loader) {
    @apply opacity-0;
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }

  /* Focus состояния */
  .ui-button:focus-visible {
    @apply ring-2 ring-(--accent) ring-offset-2 ring-offset-(--surface-page);
  }

  /* Active состояния */
  .ui-button:active:not(.ui-button--disabled) {
    @apply scale-[0.96] transform;
  }
}
</style>
